import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell, { PageContainer, PageHeader } from "@/components/AppShell";
import { useAuth } from "@/lib/AuthContext";
import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import {
  Crown,
  LogOut,
  Save,
  Globe2,
  Languages,
  Gamepad2,
  ShieldCheck,
  Mail,
  Shield,
  ArrowUpRight,
  Trophy,
  Heart,
  X,
  Search,
  CreditCard,
  RefreshCcw,
  AlertTriangle,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import AvatarBannerUploader from "@/components/AvatarBannerUploader";

const SKILL_OPTIONS = [
  { id: "casual", label: "Casual" },
  { id: "intermediate", label: "Intermediate" },
  { id: "competitive", label: "Competitive" },
  { id: "pro", label: "Pro" },
];

const EXP_OPTIONS = [
  { id: "rookie", label: "Rookie" },
  { id: "veteran", label: "Veteran" },
  { id: "hardcore", label: "Hardcore" },
];

export default function ProfilePage() {
  const { t } = useT();
  const { user, profile, saveProfile, logout } = useAuth();
  const navigate = useNavigate();
  const [games, setGames] = useState([]);
  const [allLanguages, setAllLanguages] = useState([]);
  const [resending, setResending] = useState(false);
  const [logoutAllBusy, setLogoutAllBusy] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);

  // Local editable state — synced from profile
  const [selectedLanguages, setSelectedLanguages] = useState(profile?.languages || []);
  const [skillLevel, setSkillLevel] = useState(profile?.skill_level || "");
  const [experienceLevel, setExperienceLevel] = useState(profile?.experience_level || "");

  useEffect(() => {
    api.get("/games").then((r) => setGames(r.data.items)).catch(() => {});
    api.get("/meta/languages").then((r) => setAllLanguages(r.data.items)).catch(() => {});
  }, []);

  // Re-sync when AuthContext profile updates
  useEffect(() => {
    if (profile?.languages) setSelectedLanguages(profile.languages);
    if (profile?.skill_level) setSkillLevel(profile.skill_level);
    if (profile?.experience_level) setExperienceLevel(profile.experience_level);
  }, [profile?.languages, profile?.skill_level, profile?.experience_level]);

  const handleResendVerify = async () => {
    setResending(true);
    try {
      await api.post("/auth/verify-email/send");
      toast.success(t("profile.verify_sent") || "Bestätigungs-Link wurde gesendet. Check deine Inbox.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "object" ? detail?.message : detail;
      toast.error(msg || (t("profile.verify_failed") || "Konnte Mail nicht senden. Kurz warten, dann erneut."));
    } finally {
      setResending(false);
    }
  };

  const handleLogoutAll = async () => {
    if (!confirm(t("profile.logout_all_confirm") || "Auf allen Geräten abmelden?")) return;
    setLogoutAllBusy(true);
    try {
      await api.post("/auth/logout-all");
      toast.success(t("profile.logout_all_done") || "Überall abgemeldet.");
      logout();
      navigate("/login");
    } catch (_e) {
      toast.error(t("profile.logout_all_failed") || "Abmelden fehlgeschlagen.");
    } finally {
      setLogoutAllBusy(false);
    }
  };

  // Persist a partial profile change to backend + AuthContext.
  const persistProfile = async (patch) => {
    if (!user?.id) return;
    const merged = { ...profile, ...patch };
    saveProfile(merged);
    try {
      await api.post("/onboarding", {
        user_id: user.id,
        nickname: merged.nickname || undefined,
        country: merged.country,
        language: merged.language,
        languages: merged.languages || [],
        games: merged.games || [],
        avatar: merged.avatar || "",
        skill_level: merged.skill_level || undefined,
        experience_level: merged.experience_level || undefined,
      });
    } catch (_e) {
      // Non-fatal — local state already updated.
    }
  };

  const toggleGame = async (name) => {
    const current = profile?.games || [];
    const next = current.includes(name)
      ? current.filter((g) => g !== name)
      : [...current, name];
    await persistProfile({ games: next });
  };

  const toggleLanguage = (code) => {
    setSelectedLanguages((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code].slice(0, 5)
    );
  };

  const handleSaveMeta = async () => {
    setSavingMeta(true);
    try {
      await persistProfile({
        languages: selectedLanguages,
        skill_level: skillLevel || null,
        experience_level: experienceLevel || null,
      });
      toast.success(t("profile.meta_saved") || "Profil-Details gespeichert.");
    } catch (_e) {
      toast.error(t("profile.meta_failed") || "Speichern fehlgeschlagen.");
    } finally {
      setSavingMeta(false);
    }
  };

  const favoriteGames = profile?.games || [];
  const otherGames = useMemo(
    () => games.filter((g) => !favoriteGames.includes(g.name)),
    [games, favoriteGames]
  );

  return (
    <AppShell>
      <PageContainer>
        <PageHeader kicker={t("page.profile.kicker")} title={t("page.profile.title")} subtitle={t("page.profile.sub")} />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <section className="lg:col-span-2 rounded-2xl border border-white/5 bg-zinc-900/50 p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-neon to-blue-500 grid place-items-center text-black font-display font-black text-2xl">
                {(profile?.nickname || "P").slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1">
                <h2 className="font-display text-2xl font-bold" data-testid="profile-public-name">
                  @{profile?.nickname || "—"}
                </h2>
                <div className="text-sm text-zinc-500" data-testid="profile-private-email">{user?.email}</div>
                <div className="mt-1 text-[11px] uppercase tracking-widest text-zinc-600">
                  {t("profile.private_email_note") || "Email privat — nie öffentlich sichtbar"}
                </div>
              </div>
              {user?.isPremium && (
                <Badge className="bg-gradient-to-br from-yellow-400 to-orange-500 text-black border-0">
                  <Crown size={12} className="mr-1" />
                  Premium
                </Badge>
              )}
            </div>

            <Separator className="my-6 bg-white/5" />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nickname" className="text-zinc-300">{t("page.profile.display_name")}</Label>
                <Input
                  id="nickname"
                  className="mt-1.5 h-11 rounded-xl bg-zinc-950 border-white/10"
                  value={profile?.nickname || ""}
                  readOnly
                  data-testid="profile-nickname-input"
                />
                <p className="mt-1 text-[11px] text-zinc-500">
                  {t("profile.nickname_locked") || "Nickname kannst du im Onboarding ändern."}
                </p>
              </div>
              <div>
                <Label className="text-zinc-300">{t("page.profile.email")}</Label>
                <Input
                  value={user?.email || ""}
                  disabled
                  className="mt-1.5 h-11 rounded-xl bg-zinc-950 border-white/10"
                />
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <InfoTile icon={Globe2} label={t("page.profile.country")} value={`${profile?.flag || ""} ${profile?.countryName || profile?.country || "—"}`} />
              <InfoTile icon={Languages} label={t("page.profile.language")} value={(selectedLanguages.length || (profile?.language ? 1 : 0)) + " " + (t("profile.langs_active") || "aktiv")} />
              <InfoTile icon={Gamepad2} label={t("page.profile.games")} value={(profile?.games?.length || 0) + " " + t("page.profile.games_saved")} />
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <Button
                variant="outline"
                className="rounded-full bg-transparent border-white/10 hover:bg-white/5 text-white hover:text-white"
                onClick={() => {
                  logout();
                  navigate("/");
                }}
                data-testid="profile-logout-btn"
              >
                <LogOut size={16} className="mr-2" />
                {t("profile.logout") || "Sign out"}
              </Button>
              <Button
                variant="outline"
                disabled={logoutAllBusy}
                className="rounded-full bg-transparent border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                onClick={handleLogoutAll}
                data-testid="profile-logout-all-btn"
              >
                <Shield size={16} className="mr-2" />
                {logoutAllBusy ? "…" : (t("profile.logout_all") || "Auf allen Geräten abmelden")}
              </Button>
            </div>

            {/* Email verification banner */}
            {user?.provider === "email" && user?.email_verified !== true && (
              <div
                className="mt-6 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid="profile-verify-banner"
              >
                <div className="flex items-center gap-2 text-amber-300 text-sm">
                  <Mail size={16} />
                  <span className="font-semibold">
                    {t("profile.verify_h") || "E-Mail noch nicht bestätigt"}
                  </span>
                </div>
                <p className="text-xs text-zinc-400 flex-1">
                  {t("profile.verify_sub") || "Bestätige deine E-Mail, damit wir Passwort-Resets & wichtige Account-Infos schicken können."}
                </p>
                <Button
                  size="sm"
                  disabled={resending}
                  onClick={handleResendVerify}
                  data-testid="profile-resend-verify-btn"
                  className="rounded-full bg-amber-500 text-black hover:bg-amber-400 font-semibold"
                >
                  {resending ? "…" : (t("profile.verify_resend") || "Erneut senden")}
                </Button>
              </div>
            )}
            {user?.email_verified === true && (
              <div
                className="mt-6 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-3 flex items-center gap-2 text-emerald-300 text-xs"
                data-testid="profile-verified-banner"
              >
                <ShieldCheck size={14} />
                <span>{t("profile.verified") || "E-Mail bestätigt · Account gesichert"}</span>
              </div>
            )}
          </section>

          <aside className="rounded-2xl border border-white/5 bg-zinc-900/50 p-6">
            <h3 className="font-display text-lg font-bold">{t("page.profile.membership")}</h3>
            <p className="text-sm text-zinc-400 mt-1">
              {user?.isPremium ? t("page.profile.premium_on") : t("page.profile.premium_off")}
            </p>
            <Button
              className="mt-4 w-full rounded-full bg-neon text-black hover:bg-neon-hover font-bold shadow-neon"
              onClick={() => navigate("/app/premium")}
              data-testid="profile-premium-cta"
            >
              <Crown size={16} className="mr-2" />
              {user?.isPremium ? t("page.profile.manage_premium") : t("dash.go_premium")}
            </Button>

            <Separator className="my-5 bg-white/5" />

            <Link
              to="/app/players"
              className="block rounded-xl border border-neon/25 bg-neon/5 p-4 hover:bg-neon/10 transition-colors group"
              data-testid="profile-find-players-cta"
            >
              <div className="inline-flex items-center gap-2 text-neon text-sm font-semibold">
                <Search size={14} />
                {t("profile.find_players_h") || "Mitspieler finden"}
                <ArrowUpRight size={14} className="opacity-60 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
              <p className="mt-1 text-xs text-zinc-400">
                {t("profile.find_players_sub") || "Match-Score basierend auf Spiel, Land, Sprache und Skill."}
              </p>
            </Link>
          </aside>
        </div>

        {/* PROFILE META — Languages + Skill + Experience */}
        <section
          className="mt-8 rounded-2xl border border-white/5 bg-zinc-900/50 p-6"
          data-testid="profile-meta-section"
        >
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <h3 className="font-display text-xl font-bold inline-flex items-center gap-2">
              <Trophy size={18} className="text-neon" />
              {t("profile.meta_h") || "Spieler-Profil"}
            </h3>
            <Button
              size="sm"
              disabled={savingMeta}
              onClick={handleSaveMeta}
              data-testid="profile-meta-save-btn"
              className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold"
            >
              <Save size={14} className="mr-1.5" />
              {savingMeta ? "…" : (t("profile.meta_save") || "Speichern")}
            </Button>
          </div>
          <p className="text-xs text-zinc-400 mb-5">
            {t("profile.meta_sub") || "Diese Angaben verbessern deinen Match-Score in der Spielersuche."}
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Languages */}
            <div data-testid="profile-langs-block">
              <Label className="text-zinc-300 inline-flex items-center gap-1.5 mb-2">
                <Languages size={14} className="text-neon" />
                {t("profile.langs_h") || "Sprachen"}{" "}
                <span className="text-[10px] text-zinc-500 font-mono">
                  ({selectedLanguages.length}/5)
                </span>
              </Label>
              <div className="flex flex-wrap gap-2">
                {allLanguages.map((l) => {
                  const active = selectedLanguages.includes(l.code);
                  return (
                    <button
                      key={l.code}
                      type="button"
                      onClick={() => toggleLanguage(l.code)}
                      data-testid={`profile-lang-${l.code}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                        active
                          ? "bg-neon text-black border-neon shadow-neon"
                          : "bg-white/5 border-white/10 text-zinc-300 hover:border-neon/40"
                      }`}
                    >
                      {l.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Skill level */}
            <div data-testid="profile-skill-block">
              <Label className="text-zinc-300 inline-flex items-center gap-1.5 mb-2">
                <Trophy size={14} className="text-neon" />
                {t("profile.skill_h") || "Skill-Level"}
              </Label>
              <div className="flex flex-wrap gap-2">
                {SKILL_OPTIONS.map((s) => {
                  const active = skillLevel === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSkillLevel(active ? "" : s.id)}
                      data-testid={`profile-skill-${s.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                        active
                          ? "bg-neon text-black border-neon shadow-neon"
                          : "bg-white/5 border-white/10 text-zinc-300 hover:border-neon/40"
                      }`}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Experience level */}
            <div data-testid="profile-exp-block">
              <Label className="text-zinc-300 inline-flex items-center gap-1.5 mb-2">
                <Trophy size={14} className="text-neon" />
                {t("profile.exp_h") || "Erfahrung"}
              </Label>
              <div className="flex flex-wrap gap-2">
                {EXP_OPTIONS.map((e) => {
                  const active = experienceLevel === e.id;
                  return (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => setExperienceLevel(active ? "" : e.id)}
                      data-testid={`profile-exp-${e.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                        active
                          ? "bg-neon text-black border-neon shadow-neon"
                          : "bg-white/5 border-white/10 text-zinc-300 hover:border-neon/40"
                      }`}
                    >
                      {e.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* FAVORITE GAMES — clickable to /app/players?game= */}
        <section className="mt-8 rounded-2xl border border-white/5 bg-zinc-900/50 p-6" data-testid="profile-favorites-section">
          <h3 className="font-display text-xl font-bold inline-flex items-center gap-2 mb-1">
            <Heart size={16} className="text-neon" />
            {t("page.profile.favorite_games")}
          </h3>
          <p className="text-xs text-zinc-400 mb-4">
            {t("profile.favs_hint") || "Klicke ein Spiel, um direkt Mitspieler dafür zu finden. Das ❤︎ entfernt den Favoriten."}
          </p>

          {favoriteGames.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/10 bg-zinc-950/40 p-6 text-center">
              <p className="text-sm text-zinc-400">
                {t("profile.favs_empty") || "Noch keine Lieblingsgames. Wähle unten eines aus."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {favoriteGames.map((name) => {
                const meta = games.find((x) => x.name === name);
                return (
                  <div
                    key={name}
                    data-testid={`profile-fav-${name}`}
                    className="relative overflow-hidden rounded-xl border border-neon shadow-neon group"
                  >
                    <Link
                      to={`/app/players?game=${encodeURIComponent(name)}`}
                      className="block"
                      data-testid={`profile-fav-link-${name}`}
                    >
                      {meta?.cover && (
                        <img
                          src={meta.cover}
                          alt={name}
                          className="w-full h-24 object-cover opacity-90 group-hover:opacity-100 transition"
                          loading="lazy"
                        />
                      )}
                      <div className="p-2 bg-zinc-950/80">
                        <div className="font-bold text-xs truncate">{name}</div>
                        <div className="text-[10px] text-neon inline-flex items-center gap-1">
                          {t("profile.fav_find") || "Mitspieler finden"} <ArrowUpRight size={10} />
                        </div>
                      </div>
                    </Link>
                    <button
                      type="button"
                      onClick={() => toggleGame(name)}
                      data-testid={`profile-fav-remove-${name}`}
                      title={t("profile.fav_remove") || "Aus Favoriten entfernen"}
                      className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/70 backdrop-blur grid place-items-center text-rose-300 hover:bg-rose-500/30 transition"
                    >
                      <X size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Add more games */}
          {otherGames.length > 0 && (
            <div className="mt-6">
              <p className="text-[11px] uppercase tracking-widest text-zinc-500 font-mono mb-2">
                {t("profile.add_more") || "Mehr Spiele hinzufügen"}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {otherGames.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => toggleGame(g.name)}
                    data-testid={`profile-add-${g.id}`}
                    className="relative overflow-hidden rounded-xl border border-white/5 hover:border-white/20 text-left transition-all group"
                  >
                    <img src={g.cover} alt={g.name} className="w-full h-20 object-cover opacity-50 group-hover:opacity-80 transition" loading="lazy" />
                    <div className="p-2 bg-zinc-950/80">
                      <div className="font-bold text-xs truncate text-zinc-300">+ {g.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </section>

        <SubscriptionSection t={t} />

        <PublicProfileEditor t={t} />

        <EmailPreferencesSection t={t} />

        <DangerZone t={t} navigate={navigate} logout={logout} />
      </PageContainer>
    </AppShell>
  );
}

function InfoTile({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-white/5 bg-zinc-950/50 p-3">
      <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-500 font-mono">
        <Icon size={12} className="text-neon" />
        {label}
      </div>
      <div className="mt-1 font-display font-bold">{value}</div>
    </div>
  );
}


function SubscriptionSection({ t }) {
  const [sub, setSub] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [cancelOpen, setCancelOpen] = React.useState(false);
  const [reactOpen, setReactOpen] = React.useState(false);
  const { setPremium } = useAuth();

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/payments/subscription/status");
      setSub(r.data);
    } catch (_e) {
      setSub(null);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/payments/subscription/cancel");
      toast.success(t("sub.cancel_ok") || "Premium wird zum Ende der Laufzeit gekündigt.");
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (t("sub.cancel_failed") || "Kündigung fehlgeschlagen."));
    } finally {
      setBusy(false);
      setCancelOpen(false);
    }
  };

  const reactivate = async () => {
    setBusy(true);
    try {
      await api.post("/payments/subscription/reactivate");
      toast.success(t("sub.reactivate_ok") || "Premium wurde reaktiviert.");
      setPremium(true);
      await load();
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (t("sub.reactivate_failed") || "Reaktivierung fehlgeschlagen."));
    } finally {
      setBusy(false);
      setReactOpen(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/portal/session");
      if (r.data?.url) window.location.href = r.data.url;
    } catch (err) {
      const msg = err?.response?.data?.detail?.message || err?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (t("sub.portal_failed") || "Portal konnte nicht geöffnet werden."));
      setBusy(false);
    }
  };

  if (loading || !sub) return null;
  if (!sub.subscription_id) return null;

  const dt = sub.current_period_end ? new Date(sub.current_period_end * 1000) : null;
  const periodEnd = dt ? dt.toLocaleDateString() : "—";

  return (
    <section
      className="mt-8 rounded-2xl border border-white/5 bg-zinc-900/50 p-6"
      data-testid="profile-subscription-section"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-display text-xl font-bold inline-flex items-center gap-2">
          <CreditCard size={18} className="text-neon" />
          {t("sub.h") || "Abo verwalten"}
        </h3>
        <Badge
          className={`text-[10px] uppercase ${
            sub.cancel_at_period_end
              ? "bg-amber-500/10 text-amber-300 border border-amber-500/30"
              : "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30"
          }`}
          data-testid="profile-sub-status-badge"
        >
          {sub.cancel_at_period_end
            ? (t("sub.cancel_scheduled") || "Kündigung geplant")
            : (t("sub.active") || "Aktiv")}
        </Badge>
      </div>
      <p className="text-sm text-zinc-400 mb-5">
        {sub.cancel_at_period_end
          ? (t("sub.ends_on") || "Premium bleibt aktiv bis").concat(" ", periodEnd, ".")
          : (t("sub.renews_on") || "Nächste Abbuchung am").concat(" ", periodEnd, ".")}
      </p>

      <div className="flex flex-wrap gap-3">
        <Button
          variant="outline"
          disabled={busy}
          onClick={openPortal}
          data-testid="profile-portal-btn"
          className="rounded-full bg-transparent border-white/10 text-white hover:bg-white/5 hover:text-white"
        >
          <CreditCard size={14} className="mr-2" />
          {t("sub.portal_btn") || "Abo verwalten"}
        </Button>

        {sub.cancel_at_period_end ? (
          <Button
            disabled={busy}
            onClick={() => setReactOpen(true)}
            data-testid="profile-reactivate-btn"
            className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold shadow-neon"
          >
            <RefreshCcw size={14} className="mr-2" />
            {t("sub.reactivate_btn") || "Premium reaktivieren"}
          </Button>
        ) : (
          <Button
            variant="outline"
            disabled={busy}
            onClick={() => setCancelOpen(true)}
            data-testid="profile-cancel-btn"
            className="rounded-full bg-transparent border-amber-500/30 text-amber-300 hover:bg-amber-500/10 hover:text-amber-200"
          >
            <X size={14} className="mr-2" />
            {t("sub.cancel_btn") || "Premium kündigen"}
          </Button>
        )}
      </div>

      {/* Cancel confirmation */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent className="bg-zinc-950 border border-white/10 text-white" data-testid="cancel-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("sub.cancel_modal_h") || "Premium wirklich kündigen?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t("sub.cancel_modal_sub") ||
                "Dein Premium-Zugang bleibt bis zum Ende der aktuellen Laufzeit aktiv."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10" data-testid="cancel-modal-cancel">
              {t("sub.cancel_modal_no") || "Abbrechen"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={cancel}
              disabled={busy}
              className="bg-amber-500 text-black hover:bg-amber-400"
              data-testid="cancel-modal-confirm"
            >
              {t("sub.cancel_modal_yes") || "Premium kündigen"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reactivate confirmation */}
      <AlertDialog open={reactOpen} onOpenChange={setReactOpen}>
        <AlertDialogContent className="bg-zinc-950 border border-white/10 text-white" data-testid="reactivate-modal">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("sub.reactivate_modal_h") || "Premium reaktivieren?"}</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t("sub.reactivate_modal_sub") ||
                "Dein Premium-Abo wird wieder aktiv fortgeführt."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              {t("sub.cancel_modal_no") || "Abbrechen"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={reactivate}
              disabled={busy}
              className="bg-neon text-black hover:bg-neon-hover"
              data-testid="reactivate-modal-confirm"
            >
              {t("sub.reactivate_btn") || "Premium reaktivieren"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}


function DangerZone({ t, navigate, logout }) {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);

  const deactivate = async () => {
    setBusy(true);
    try {
      await api.post("/account/deactivate");
      toast.success(t("danger.deactivate_ok") || "Account deaktiviert. Du wirst ausgeloggt.");
      logout();
      // Hard-redirect to /login so any in-flight component that depends on the
      // user object unmounts cleanly; the global axios interceptor will catch
      // any straggling 401s during teardown.
      setTimeout(() => { window.location.href = "/login"; }, 400);
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const code = typeof raw === "string" ? raw : (raw?.error || raw?.message);
      const friendly =
        code === "not_authenticated" || code === "auth_required"
          ? "Deine Sitzung ist abgelaufen — bitte erneut einloggen."
          : code === "account_deactivated"
          ? "Dieses Konto ist bereits deaktiviert."
          : (typeof code === "string" ? code : (t("danger.deactivate_failed") || "Deaktivierung fehlgeschlagen."));
      toast.error(friendly);
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <section
      className="mt-8 rounded-2xl border border-rose-500/20 bg-rose-500/[0.03] p-6"
      data-testid="profile-danger-zone"
    >
      <h3 className="font-display text-xl font-bold inline-flex items-center gap-2 text-rose-300">
        <AlertTriangle size={18} />
        {t("danger.h") || "Danger Zone"}
      </h3>
      <p className="mt-1 text-sm text-zinc-400">
        {t("danger.sub") || "Dauerhafte Aktionen — sei dir sicher, bevor du klickst."}
      </p>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          onClick={() => setOpen(true)}
          data-testid="profile-deactivate-btn"
          className="rounded-full bg-transparent border-rose-500/40 text-rose-300 hover:bg-rose-500/10 hover:text-rose-200"
        >
          <Trash2 size={14} className="mr-2" />
          {t("danger.deactivate_btn") || "Account deaktivieren"}
        </Button>
        <span className="text-xs text-zinc-500">
          {t("danger.deactivate_hint") ||
            "Profil + Daten werden deaktiviert. Login wird danach blockiert."}
        </span>
      </div>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-zinc-950 border border-rose-500/30 text-white" data-testid="deactivate-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-rose-300">
              {t("danger.deactivate_modal_h") || "Account wirklich deaktivieren?"}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              {t("danger.deactivate_modal_sub") ||
                "Dein Profil, deine Teams und deine Daten werden deaktiviert. Dieser Vorgang kann möglicherweise nicht rückgängig gemacht werden."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
              {t("danger.deactivate_modal_no") || "Abbrechen"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deactivate}
              disabled={busy}
              className="bg-rose-500 text-white hover:bg-rose-600"
              data-testid="deactivate-modal-confirm"
            >
              {t("danger.deactivate_modal_yes") || "Account deaktivieren"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}


function PublicProfileEditor({ t }) {
  // eslint-disable-next-line no-undef
  const lang = (typeof window !== "undefined" && document.documentElement.lang) || "de";
  const isDE = (lang || "de").startsWith("de");
  const [me, setMe] = React.useState(null);
  const [bio, setBio] = React.useState("");
  const [playstyle, setPlaystyle] = React.useState("");
  const [voice, setVoice] = React.useState(false);
  const [avatarUrl, setAvatarUrl] = React.useState(null);
  const [bannerUrl, setBannerUrl] = React.useState(null);
  const [saving, setSaving] = React.useState(false);

  // Lazy import so we don't pollute the file header
  const supRef = React.useRef(null);
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mod = await import("@/lib/supabaseClient");
        supRef.current = mod.supabase;
        const ctxMod = await import("@/lib/SupabaseSessionContext");
        // tiny one-shot: wait until session ready via interval (max 6s)
        const start = Date.now();
        while (!cancelled && Date.now() - start < 6000) {
          const { data } = await mod.supabase.auth.getSession();
          if (data?.session?.user?.id) break;
          await new Promise((r) => setTimeout(r, 250));
        }
        if (cancelled) return;
        const { data: sess } = await mod.supabase.auth.getSession();
        const sb_uid = sess?.session?.user?.id;
        if (!sb_uid) return;
        const { data: prof } = await mod.supabase
          .from("profiles")
          .select("user_id, username, bio, playstyle, mic, favorite_games, languages, country, avatar_url, banner_url")
          .eq("user_id", sb_uid).maybeSingle();
        if (cancelled) return;
        setMe(prof || null);
        setBio(prof?.bio || "");
        setPlaystyle(prof?.playstyle || "");
        setVoice(!!prof?.mic);
        setAvatarUrl(prof?.avatar_url || null);
        setBannerUrl(prof?.banner_url || null);
        // suppress unused-warn
        void ctxMod;
      } catch (_e) { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async () => {
    if (!supRef.current || !me?.user_id) return;
    setSaving(true);
    try {
      await supRef.current.from("profiles").update({
        bio: bio.trim().slice(0, 280),
        playstyle: playstyle.trim().slice(0, 40),
        mic: voice,
        updated_at: new Date().toISOString(),
      }).eq("user_id", me.user_id);
      toast.success(isDE ? "Profil gespeichert" : "Profile saved");
    } catch (_e) { /* noop */ }
    finally { setSaving(false); }
  };

  // Profile completion %
  const completion = React.useMemo(() => {
    if (!me) return 0;
    let s = 0; const max = 9;
    if (me.username) s++;
    if ((me.favorite_games || []).length) s++;
    if ((me.languages || []).length) s++;
    if (me.country) s++;
    if (bio.trim()) s++;
    if (playstyle.trim()) s++;
    if (voice !== null && voice !== undefined) s++;
    if (avatarUrl) s++;
    if (bannerUrl) s++;
    return Math.round((s / max) * 100);
  }, [me, bio, playstyle, voice, avatarUrl, bannerUrl]);

  if (!me) return null;

  return (
    <section className="rounded-2xl border border-white/10 bg-zinc-900/50 p-5 sm:p-6 mt-5" data-testid="public-profile-editor">
      <div className="flex items-center justify-between gap-2 mb-2">
        <h2 className="font-display text-lg font-bold">{isDE ? "Öffentliches Profil" : "Public Profile"}</h2>
        <span className="text-[11px] font-mono text-zinc-500" data-testid="profile-completion">{isDE ? "Vollständig" : "Complete"}: {completion}%</span>
      </div>
      <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mb-5">
        <div className="h-full bg-gradient-to-r from-neon to-emerald-400 transition-all" style={{ width: `${completion}%` }} />
      </div>

      {/* Banner + Avatar uploaders */}
      <div className="space-y-4 mb-5">
        <AvatarBannerUploader
          bucket="banners"
          currentUrl={bannerUrl}
          onUploaded={setBannerUrl}
          isDE={isDE}
        />
        <AvatarBannerUploader
          bucket="avatars"
          currentUrl={avatarUrl}
          onUploaded={setAvatarUrl}
          isDE={isDE}
        />
      </div>

      <div className="space-y-4">
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">{isDE ? "Bio (max. 280 Zeichen)" : "Bio (max 280 chars)"}</label>
          <textarea
            value={bio} onChange={(e) => setBio(e.target.value.slice(0, 280))}
            rows={3}
            placeholder={isDE ? "Was sollten Mitspieler über dich wissen?" : "What should teammates know about you?"}
            className="w-full rounded-xl bg-zinc-950 border border-white/10 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-neon/50 resize-none"
            data-testid="profile-bio"
          />
          <p className="text-[10px] text-zinc-500 mt-1 text-right">{bio.length}/280</p>
        </div>
        <div>
          <label className="text-xs text-zinc-500 mb-1 block">{isDE ? "Playstyle" : "Playstyle"}</label>
          <input
            value={playstyle} onChange={(e) => setPlaystyle(e.target.value.slice(0, 40))}
            placeholder={isDE ? "z.B. taktisch, aggro, support…" : "e.g. tactical, aggro, support…"}
            className="w-full rounded-xl bg-zinc-950 border border-white/10 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-neon/50"
            data-testid="profile-playstyle"
          />
        </div>
        <label className="flex items-center justify-between gap-3 rounded-xl bg-zinc-950 border border-white/10 px-3 py-2.5" data-testid="profile-voice-toggle">
          <span className="text-sm text-zinc-300">{isDE ? "Voice-Chat aktiv (Mic)" : "Voice chat enabled (mic)"}</span>
          <Switch checked={voice} onCheckedChange={setVoice} />
        </label>
        <Button onClick={save} disabled={saving} className="rounded-full bg-neon text-black hover:bg-neon-hover font-bold w-full" data-testid="profile-save-btn">
          {saving ? (isDE ? "Speichert…" : "Saving…") : (isDE ? "Speichern" : "Save")}
        </Button>
      </div>
    </section>
  );
}


function EmailPreferencesSection({ t }) {
  const [prefs, setPrefs] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const lang = (typeof window !== "undefined" && document.documentElement.lang) || "de";
  const isDE = (lang || "de").startsWith("de");

  React.useEffect(() => {
    api.get("/me/notification-preferences")
      .then((r) => setPrefs(r.data))
      .catch(() => setPrefs({
        email_messages: true, email_squad_invites: true, email_friend_requests: true,
        email_accepted_requests: true, email_system: true, email_updates_news: true,
        email_marketing: false,
      }));
  }, []);

  const toggle = async (key, value) => {
    setPrefs((p) => ({ ...p, [key]: value }));
    setSaving(true);
    try {
      await api.put("/me/notification-preferences", { [key]: value });
    } catch (_e) {
      setPrefs((p) => ({ ...p, [key]: !value }));
    } finally {
      setSaving(false);
    }
  };

  if (!prefs) return null;
  const rows = [
    { k: "email_messages", de: "Direktnachrichten", en: "Direct messages" },
    { k: "email_squad_invites", de: "Squad-Einladungen", en: "Squad invites" },
    { k: "email_friend_requests", de: "Freundschaftsanfragen", en: "Friend requests" },
    { k: "email_accepted_requests", de: "Akzeptierte Anfragen", en: "Accepted requests" },
    { k: "email_system", de: "System-Updates", en: "System announcements" },
    { k: "email_updates_news", de: "News & Updates", en: "Updates & news" },
    { k: "email_marketing", de: "Marketing / Promotions", en: "Marketing / promos" },
  ];

  return (
    <section className="rounded-2xl border border-white/10 bg-zinc-900/50 p-5 sm:p-6 mt-5" data-testid="email-prefs">
      <h2 className="font-display text-lg font-bold mb-1 inline-flex items-center gap-2">
        {isDE ? "E-Mail-Benachrichtigungen" : "Email Notifications"}
        {saving && <span className="text-[10px] text-zinc-500 font-normal">{isDE ? "speichert…" : "saving…"}</span>}
      </h2>
      <p className="text-xs text-zinc-500 mb-4">
        {isDE
          ? "Lege fest, wofür du E-Mails bekommen willst. In-App-Benachrichtigungen bleiben aktiv."
          : "Choose what to receive emails for. In-app notifications stay on."}
      </p>
      <div className="divide-y divide-white/5">
        {rows.map((row) => (
          <label key={row.k} className="flex items-center justify-between py-3" data-testid={`email-pref-${row.k}`}>
            <span className="text-sm text-zinc-200">{isDE ? row.de : row.en}</span>
            <Switch checked={!!prefs[row.k]} onCheckedChange={(v) => toggle(row.k, v)} />
          </label>
        ))}
      </div>
    </section>
  );
}
